import React from 'react';
import { MapPin, Phone, Clock, Navigation } from 'lucide-react';
import SEO from '../components/SEO';

const Location = () => {
  const address = "543 FM 416, Streetman, TX 75859";
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d108852.28587132034!2d-96.381362!3d31.968032!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8648afe763327685%3A0xc49b78e38510dc8!2sStreetman%2C%20TX%2075859!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage Units Near Richland Chambers Lake | FM 416 | V&D Storage"
        description="V&D Storage is located on FM 416, minutes from Richland Chambers Lake. Easy access, concrete driveways, electronic gate. Get directions today."
        canonical="/location"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="location-title">
            Self Storage Units Near Richland Chambers Lake
          </h1>
          <p className="text-xl text-green-100">
            Conveniently located on FM 416, near Richland Chambers Lake
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-4">
              V&D Storage is locally owned and proudly serves the Richland Chambers Lake community.
              Our convenient location on <strong>FM 416</strong> makes us the perfect stop
              for residents and businesses in:
            </p>
            <ul className="text-lg text-gray-700 space-y-2 list-disc list-inside">
              <li><strong>Streetman (75859)</strong></li>
              <li><strong>Richland Chambers Reservoir Area</strong></li>
              <li><strong>Fairfield / Corsicana</strong></li>
            </ul>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-testid="map-container">
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Interskate Self Storage Location"
                  className="w-full h-[500px]"
                ></iframe>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6" data-testid="location-info">
                <div className="flex items-start mb-6">
                  <MapPin className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Address</h3>
                    <p className="text-gray-700">
                      543 FM 416<br />
                      Streetman, TX 75859
                    </p>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline mt-2 inline-block"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>

                <div className="flex items-start mb-6">
                  <Phone className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                    <a href="tel:8007687640" className="text-green-600 hover:underline text-lg">
                      (800) 768-7640
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Hours</h3>
                    <div className="text-gray-700 space-y-1">
                      <p><strong>Office Hours:</strong></p>
                      <p>Mon-Fri: 9:00 AM - 6:00 PM</p>
                      <p>Sat: 9:00 AM - 3:00 PM</p>
                      <p>Sun: Closed</p>
                      <p className="mt-3"><strong>Gate Access:</strong></p>
                      <p>6:00 AM - 10:00 PM Daily</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                <Navigation className="h-8 w-8 text-green-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Directions</h3>
                <p className="text-gray-700 text-sm">
                  We are located on FM 416, easily accessible from the lake and surrounding areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Locals Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Why Locals Choose Us
          </h2>
          <p className="text-lg text-gray-700 text-center mb-8">
            We are just minutes from <strong>Richland Chambers Reservoir</strong>, making us the ideal spot to
            store fishing gear, camping equipment, and small boats. Avoid the high prices of marine
            storage—keep your gear secure and accessible slightly off the water.
          </p>
          <div className="text-center">
            <a
              href="https://vdstoragestreetman.storageunitsoftware.com/pages/rent"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-md mr-4"
              data-testid="reserve-button"
            >
              Reserve a Unit
            </a>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-colors"
              data-testid="contact-button"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;